import { useCallback, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import { BASEMAPS } from "../lib/basemap";
import { FACILITY_COLOR } from "../lib/facilities";
import { SURFACE_PIN_HINT } from "../lib/facilityPin";
import { GROUND_PIN_FROM_MAP, groundPin, isGroundPin } from "../lib/groundEdit";
import MapControlPill from "./kit/MapControlPill";
import MapFullScreen from "./kit/MapFullScreen";
import MapFullScreenSync from "./kit/MapFullScreenSync";

/**
 * Issue #548 — the pin-a-surface gesture, shared between its two hosts.
 *
 * #379 built pin-by-tap and drag-the-pin for a GROUND correction, on the
 * detail dialog's satellite map. This file is that machinery made reusable
 * for a SURFACE pin — reused, not forked: `PinDropCatcher` moved here from
 * `GroundsMap.jsx` (both its callers now import it), the `via` vocabulary
 * is `lib/groundEdit.js`'s, and the marker/hint follow #379's decisions
 * (dragend not drag, 44px drag target, pointer-events-none on the hint)
 * for #379's stated reasons.
 *
 * Two hosts, one gesture:
 *   - `GroundsMap.jsx`'s ground detail dialog mounts `SurfacePinLayer`
 *     inside the map it already has, while the add-surface form below it
 *     is open — the main path, because that map is unambiguous by
 *     construction: you opened one ground, the pin lands inside it.
 *   - `FacilityAddForm.jsx` mounts `SurfacePinMiniMap` when NO host map is
 *     wired (the admin venue console), so the console gets the same
 *     gesture instead of keeping the coordinate-typing experience by
 *     default.
 */

/**
 * Click anywhere on the map -> call `onDrop(latlng)`.
 *
 * Three callers, one gesture: on the directory map it drops a pin for a
 * NEW ground (#57); on the detail map, while #131's correction form is
 * open, it moves the pin of the ground already there (#379); and while
 * #542's add-surface form is open, it places the SURFACE's pin (#548).
 * Leaflet stops propagation from its own controls, so the basemap switcher
 * is not a click on the map in any case.
 */
export function PinDropCatcher({ onDrop }) {
  useMapEvents({ click(e) { onDrop({ lat: e.latlng.lat, lng: e.latlng.lng }); } });
  return null;
}

/**
 * The draft surface pin's icon. Each trait is doing work, all of it
 * settled elsewhere and matched here:
 *   - ROUND with a violet fill — the facility family's shape and colour
 *     (#166's markers, `makeFacilityIcon`), because this pin will BECOME
 *     one of those markers the moment the row saves;
 *   - 44px — #379's rule verbatim: a marker you only tap can be 34px, the
 *     one marker a thumb has to grab and MOVE cannot;
 *   - "+" glyph — the app's mark for "this position is not settled yet"
 *     (the pending ground pin and #379's movable pin both wear it);
 *   - anchored at its CENTRE — a circle has no tip, and a teardrop anchor
 *     would draw it half a marker north of where the finger put it.
 * Built once at module load for `groundIcons`' reason: leaflet is handed
 * the instance, and a fresh `L.DivIcon` per render tears down and rebuilds
 * the marker's DOM node on every unrelated state change.
 */
export const SURFACE_DRAFT_PIN_TITLE = "New surface pin (or type the Latitude and Longitude below)";
const SURFACE_DRAFT_PIN_SIZE = 44;
export const surfaceDraftPinIcon = new L.DivIcon({
  className: "facility-marker",
  html: `<div aria-hidden="true" data-testid="surface-draft-pin" style="width:${SURFACE_DRAFT_PIN_SIZE}px;height:${SURFACE_DRAFT_PIN_SIZE}px;border-radius:50%;background:${FACILITY_COLOR};border:3px solid #fff;box-shadow:0 6px 14px rgba(0,0,0,0.25);display:flex;align-items:center;justify-content:center;">
    <span style="color:white;font-weight:900;font-size:15px;line-height:1;">+</span></div>`,
  iconSize: [SURFACE_DRAFT_PIN_SIZE, SURFACE_DRAFT_PIN_SIZE],
  iconAnchor: [SURFACE_DRAFT_PIN_SIZE / 2, SURFACE_DRAFT_PIN_SIZE / 2],
});

/**
 * The in-map half of the gesture: the tap catcher, and the draggable draft
 * marker once a position exists. Mounted INSIDE a `MapContainer` — the
 * host decides when the map is this input (one write mode at a time: never
 * while a ground correction owns the same taps).
 *
 * Every move is stamped `via: GROUND_PIN_FROM_MAP`, so the form can tell a
 * map gesture from its own typing echoed back (#379's `via` discipline,
 * `lib/groundEdit.js`). `dragend`, not `drag`: leaflet fires `drag` per
 * animation frame, and each one would re-render the form's fields.
 */
export function SurfacePinLayer({ pin, onMove }) {
  return (
    <>
      <PinDropCatcher onDrop={(pos) => onMove({ ...pos, via: GROUND_PIN_FROM_MAP })} />
      {isGroundPin(pin) && (
        <Marker
          position={[pin.lat, pin.lng]}
          icon={surfaceDraftPinIcon}
          // #1176: a focusable pin needs a name. Leaflet cannot drag from
          // the keyboard; the coordinate fields beside the map are the
          // keyboard route to the same position.
          title={SURFACE_DRAFT_PIN_TITLE}
          draggable
          eventHandlers={{
            dragend: (e) => {
              const moved = e.target.getLatLng();
              onMove({ lat: moved.lat, lng: moved.lng, via: GROUND_PIN_FROM_MAP });
            },
          }}
        />
      )}
    </>
  );
}

/**
 * The hint that the map has become an input, ON the map — #379's reasoning
 * verbatim: an affordance nobody is told about is one nobody uses, and the
 * person placing a pin is looking at the imagery, not the form below it.
 * `pointer-events-none` so the hint never eats the tap it is describing.
 * Rendered as a sibling of the `MapContainer`, inside its relative wrapper.
 */
export function SurfacePinHint({ clearPill = false }) {
  return (
    <div
      // `clearPill` (#1669): reserve the map control pill's column (a 44px button and its margin) on both sides, so the
      // strip stays centred and never paints over the full-screen button.
      className={`pointer-events-none absolute inset-x-0 top-0 z-[1000] flex justify-center p-2 ${clearPill ? "px-[60px]" : ""}`}
      data-testid="surface-pin-hint"
      role="status"
    >
      <p className="rounded-full bg-slate-900/85 px-3 py-1 text-[11px] font-semibold text-white shadow">
        {SURFACE_PIN_HINT}
      </p>
    </div>
  );
}

/**
 * A small pick-a-point map for a host with no map of its own — the admin
 * venue console's `FacilityAddForm`. Satellite on purpose and without a
 * switcher: `lib/basemap.js`'s whole argument — imagery is what makes one
 * square of turf tellable from the next — and this map exists only for the
 * seconds it takes to place one pin. Centred on the venue's own pin at
 * zoom 17, #379's recognition zoom. Its one control is the pill with full screen only (#1669). Renders NOTHING when the venue is
 * unpinned: with no centre there is nothing honest to show, and the
 * coordinate fields remain the fallback (stated where the form offers
 * them).
 */
export function SurfacePinMiniMap({ ground, pin, onMove, testId }) {
  const centre = groundPin(ground);
  const satellite = BASEMAPS.find((b) => b.id === "satellite");
  if (!centre || !satellite) return null;
  // #1669 (D449, D467): the one map control, with full screen only. The basemap is fixed on satellite (above), so
  // the pill draws no map/satellite toggle. The same map and the same pin handlers go full screen (`MapFullScreen`
  // moves the box, it does not mount a second map), so placing a pin works inline and in the overlay.
  return (
    <div className="mt-1">
      <MapFullScreen testId={testId} controlInPill>
        {() => (
          <>
            <MapContainer
              center={[centre.lat, centre.lng]}
              zoom={17}
              style={{ height: "100%", width: "100%" }}
              scrollWheelZoom={false}
              zoomControl={false}
            >
              <TileLayer url={satellite.url} attribution={satellite.attribution} maxZoom={satellite.maxZoom} />
              <MapControlPill basemap="satellite" showBasemap={false} />
              {/* On Close the box shrinks and Leaflet keeps the centre, so a pin placed far from it would be clipped: bring it back. */}
              <MapFullScreenSync onResized={(map) => { if (isGroundPin(pin)) map.panInside([pin.lat, pin.lng], { padding: [56, 56], animate: false }); }} />
              <SurfacePinLayer pin={pin} onMove={onMove} />
            </MapContainer>
            <SurfacePinHint clearPill />
          </>
        )}
      </MapFullScreen>
    </div>
  );
}

/**
 * The controller a HOST PAGE holds so its own map and the add-surface form
 * can share one pin — #379's shape ("the MAP is the other half of the same
 * control", held on the page, `GroundsMap.jsx`'s `correctionPin`) packaged
 * as a hook so the page grows one line, not a state machine.
 *
 * `open()` starts a pinning session with NO pin — the surface has no
 * position until somebody gives it one; seeding from the venue pin would
 * invent exactly the guessed position `lib/facilities.js` refuses to draw.
 * `close()` drops everything: a half-placed pin must not survive a Cancel
 * (#379's rule for the correction pin). `setPin` ignores writes while no
 * session is open, so a stray `dragend` racing a close can never resurrect
 * a pin into a form that is gone.
 */
export function useSurfacePinDraft() {
  const [state, setState] = useState({ active: false, pin: null });
  const open = useCallback(() => setState({ active: true, pin: null }), []);
  const close = useCallback(() => setState({ active: false, pin: null }), []);
  const setPin = useCallback((pin) => setState((s) => (s.active ? { ...s, pin } : s)), []);
  return useMemo(
    () => ({ active: state.active, pin: state.pin, open, close, setPin }),
    [state, open, close, setPin],
  );
}
